/**
 * AchievementEngine - Badge evaluation for all activity types.
 *
 * Extended from bumblebee BadgeEvaluator. Evaluates earned achievements
 * against a context object containing activity results and curriculum progress.
 *
 * @see GitHub Issue #852
 */

import AchievementDefinitions from './AchievementDefinitions';

export default class AchievementEngine {
    /**
     * Evaluate which new badges have been earned.
     *
     * @param {object} context
     * @param {string} context.activityId - Completed activity ID
     * @param {string} context.activityType - Activity type
     * @param {number} context.stars - Stars earned (1-3)
     * @param {number} context.attempts - Number of attempts
     * @param {number} context.hintsUsed - Hints consumed
     * @param {object} context.progress - Full curriculum progress map
     * @param {string[]} context.earnedBadges - Already-earned badge IDs
     * @returns {string[]} Array of newly earned badge IDs
     */
    static evaluate (context) {
        const newBadges = [];
        const definitions = AchievementDefinitions.getAll();

        for (const badge of definitions) {
            // Skip already earned
            if (context.earnedBadges && context.earnedBadges.includes(badge.id)) {
                continue;
            }

            // Evaluate condition
            if (AchievementEngine._checkCondition(badge, context)) {
                newBadges.push(badge.id);
            }
        }

        return newBadges;
    }

    /**
     * @private
     */
    static _checkCondition (badge, context) {
        switch (badge.condition.type) {
        case 'activity-complete':
            return context.activityId === badge.condition.activityId;

        case 'activity-type-complete':
            return context.activityType === badge.condition.activityType;

        case 'stars-earned':
            return context.stars >= (badge.condition.minStars || 3);

        case 'course-complete': {
            if (!context.progress) return false;
            const courseProgress = context.progress[badge.condition.courseId];
            if (!courseProgress) return false;
            return courseProgress.completedCount >= courseProgress.totalCount;
        }

        case 'attempts-threshold':
            return context.attempts >= (badge.condition.minAttempts || 5);

        case 'no-hints':
            return context.hintsUsed === 0 && context.stars >= 1;

        case 'streak': {
            if (!context.progress) return false;
            const streak = AchievementEngine._calculateStreak(context.progress);
            return streak >= (badge.condition.minStreak || 3);
        }

        case 'total-stars': {
            if (!context.progress) return false;
            const total = AchievementEngine._totalStars(context.progress);
            return total >= (badge.condition.minTotalStars || 10);
        }

        case 'creative-solution':
            // Earned when child uses a non-standard approach (more blocks than optimal)
            return context.activityType === 'challenge' &&
                   context.stars >= 1 &&
                   context.usedBlocks > (context.optimalBlocks || 0) * 1.5;

        default:
            return false;
        }
    }

    /**
     * Calculate consecutive 3-star streak.
     * @private
     */
    static _calculateStreak (progress) {
        let streak = 0;
        const entries = Object.values(progress)
            .filter(p => p.completedAt)
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

        for (const entry of entries) {
            if (entry.stars === 3) {
                streak++;
            } else {
                break;
            }
        }
        return streak;
    }

    /**
     * Sum all stars across all activities.
     * @private
     */
    static _totalStars (progress) {
        return Object.values(progress).reduce((sum, p) => sum + (p.stars || 0), 0);
    }
}
